import { Opstartplan, Treinnummer } from "@/entities/opstartplan/domain";
import { TreinserieDisplay } from "@/features/infra";
import { TreinBadge } from "@/features/vsm/components/treinbadge.tsx";
import { Dot, Icon, Separated } from "@/shared/components";
import { capitalizeFirstChar, hourFormatInAmsterdam } from "@/shared/utils";
import { getListPreview } from "@/shared/utils/list-preview";

type OpstartplanSummaryProps = {
    opstartplan: Opstartplan;
};

export function OpstartplanSummary({ opstartplan }: Readonly<OpstartplanSummaryProps>) {
    const treinenByTreinSerie = Object.groupBy(opstartplan.treinseries, (item) => item.serie);

    const { previewItems, remainder } = getListPreview(Object.entries(treinenByTreinSerie), 5);

    return (
        <div className="grid gap-y-3 pr-3 pl-4">
            <Separated>
                {previewItems.map(([treinserie, treinen]) =>
                    treinen ? (
                        <OpstartplanTreinserieRow
                            key={treinserie}
                            treinserie={treinserie}
                            treinnummers={treinen.flatMap((t) => t.treinnummers)}
                        />
                    ) : null
                )}
                {remainder > 0 && <p className="text-s text-content-subtle font-normal">+{remainder} treinseries</p>}
            </Separated>
        </div>
    );
}

interface TreinSerieRowProps {
    treinserie: string;
    treinnummers: Treinnummer[];
}

function OpstartplanTreinserieRow({ treinserie, treinnummers }: TreinSerieRowProps) {
    const { previewItems, remainder } = getListPreview(treinnummers, 5);

    return (
        <div className="grid grid-cols-[60px_4px_1fr] items-center gap-x-4">
            <h4 className="text-content-emphasis font-medium">
                <TreinserieDisplay nummer={treinserie} />
            </h4>
            <Dot size="s" />
            <ol className="flex flex-row gap-2" data-testid={`treinserie-${treinserie}`}>
                {previewItems.map((treinserie) => (
                    <TreinBadge key={treinserie.nummer} treinSerieOfNummer={treinserie.nummer}>
                        <p className="flex flex-row items-center gap-1 text-xs">
                            <span>{capitalizeFirstChar(treinserie.traject.vanStation)}</span>
                            <Icon name="arrow-right" size="xs" />
                            <span>{capitalizeFirstChar(treinserie.traject.naarStation)}</span>
                            <span>{treinserie.vertrekDatumTijd ? hourFormatInAmsterdam(treinserie.vertrekDatumTijd) : undefined}</span>
                        </p>
                    </TreinBadge>
                ))}
                {remainder > 0 && <p className="text-s text-content-subtle font-normal">+{remainder} series</p>}
            </ol>
        </div>
    );
}
